import React, { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { FEED_COLORS, FEED_FONT, RAIL_COLUMN } from './feedTokens';

export type RailMarkerVariant = 'now' | 'time' | 'day' | 'today' | 'continue';

export interface RailMarkerProps {
  variant: RailMarkerVariant;
  weekday?: string;
  day?: string;
  time?: string;
  meridiem?: string;
  showLine?: boolean;
}

export const RailMarker = ({
  variant,
  weekday,
  day,
  time,
  meridiem,
  showLine = true,
}: RailMarkerProps): ReactElement => {
  const line = showLine ? (
    <Box
      sx={{
        width: '2px',
        flex: '1 0 0',
        minHeight: '8px',
        borderRadius: '9999px',
        backgroundColor: FEED_COLORS.line,
      }}
    />
  ) : null;

  return (
    <Box
      sx={{
        width: `${RAIL_COLUMN}px`,
        flex: `0 0 ${RAIL_COLUMN}px`,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        pt: '8px',
        minHeight: 0,
      }}
    >
      {variant === 'now' && (
        <>
          <Typography
            sx={{
              fontFamily: FEED_FONT,
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '16px',
              color: FEED_COLORS.success,
            }}
          >
            Now
          </Typography>
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '9999px',
              backgroundColor: FEED_COLORS.success,
              boxShadow: '0 0 0 3px rgba(30, 122, 67, 0.22)',
              flexShrink: 0,
            }}
          />
          {line}
        </>
      )}

      {variant === 'time' && (
        <>
          <Typography
            sx={{
              fontFamily: FEED_FONT,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: FEED_COLORS.textPrimary,
            }}
          >
            {time}
          </Typography>
          <Typography
            sx={{
              fontFamily: FEED_FONT,
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: FEED_COLORS.textSecondary,
            }}
          >
            {meridiem}
          </Typography>
          {line}
        </>
      )}

      {(variant === 'day' || variant === 'today') && (
        <>
          <Typography
            sx={{
              fontFamily: FEED_FONT,
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: FEED_COLORS.textSecondary,
            }}
          >
            {variant === 'today' ? 'Today' : weekday}
          </Typography>
          <Typography
            sx={{
              fontFamily: FEED_FONT,
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '26px',
              color: FEED_COLORS.textPrimary,
              minHeight: '26px',
            }}
          >
            {variant === 'day' ? day : '\u200b'}
          </Typography>
          {line}
        </>
      )}

      {variant === 'continue' && (
        <>
          <Box
            sx={{
              width: '8px',
              height: '8px',
              mt: '6px',
              borderRadius: '9999px',
              backgroundColor: FEED_COLORS.line,
              flexShrink: 0,
            }}
          />
          {line}
        </>
      )}
    </Box>
  );
};
